"use client";

import * as React from "react";
import Image from "next-shims/Image";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import CallOutlinedIcon from "@mui/icons-material/CallOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";

export type Agent = {
  name: string;
  roleKey: string; // i18n key e.g. "agent_owner_role"
  phone?: string; // 697...
  whatsapp?: string; // if different; otherwise fallback to phone
  email?: string;
  experienceYears?: number;
  photoSrc?: string; // "/images/agents/rita.jpg" (you will add)
  areaLabel?: string; // "Halkidiki" etc.
};

function digitsOnly(s?: string) {
  return (s || "").replace(/[^\d]/g, "");
}

// Αν είναι 10-ψηφιο GR κινητό (69xxxxxxxx), βάζει 30 μπροστά
function normalizeGRPhone(raw?: string) {
  const d = digitsOnly(raw);
  if (!d) return "";
  if (d.startsWith("30")) return d;
  if (d.length === 10 && d.startsWith("69")) return `30${d}`;
  return d; // fallback
}

export default function AgentCard({
  agent,
  t,
  compact = false,
}: {
  agent: Agent;
  t: (key: string, vars?: Record<string, any>) => string;
  compact?: boolean;
}) {
const phone = normalizeGRPhone(agent.phone);
const wa = normalizeGRPhone(agent.whatsapp || agent.phone);
  const email = agent.email || "";

  const showActions = Boolean(phone || wa || email);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        {/* Top row */}
        <Stack direction="row" spacing={1.6} alignItems="center">
          {agent.photoSrc ? (
            <Avatar
              sx={{
                width: compact ? 52 : 62,
                height: compact ? 52 : 62,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                <Image
                  src={agent.photoSrc}
                  alt={agent.name}
                  fill
                  sizes="64px"
                  style={{ objectFit: "cover" }}
                />
              </Box>
            </Avatar>
          ) : (
            <Avatar
              sx={{
                width: compact ? 52 : 62,
                height: compact ? 52 : 62,
                fontWeight: 900,
              }}
            >
              {agent.name?.slice(0, 1)?.toUpperCase() || "A"}
            </Avatar>
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{ fontWeight: 900, lineHeight: 1.1 }}
              variant={compact ? "subtitle1" : "h6"}
              noWrap
            >
              {agent.name}
            </Typography>

            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ mt: 0.35 }}
            >
              {t(agent.roleKey)}
              {agent.experienceYears ? (
                <> • {t("agent_experience_years", { years: agent.experienceYears })}</>
              ) : null}
            </Typography>

            {agent.areaLabel ? (
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={agent.areaLabel} />
              </Stack>
            ) : null}
          </Box>
        </Stack>

        {showActions ? (
          <>
            <Divider sx={{ my: 2 }} />

            {/* Actions */}
            <Stack spacing={1.1}>
              {phone ? (
                <Button
                  component="a"
                  href={`tel:${phone}`}
                  variant="contained"
                  size={compact ? "medium" : "large"}
                  startIcon={<CallOutlinedIcon />}
                  sx={{ borderRadius: 999, fontWeight: 800 }}
                  fullWidth
                >
                  {t("agent_call")}
                </Button>
              ) : null}

              {wa ? (
                <Button
                  component="a"
                  href={`https://wa.me/${wa}`}
                  target="_blank"
                  rel="noreferrer"
                  variant="outlined"
                  size={compact ? "medium" : "large"}
                  startIcon={<WhatsAppIcon />}
                  sx={{ borderRadius: 999, fontWeight: 800 }}
                  fullWidth
                >
                  {t("agent_whatsapp")}
                </Button>
              ) : null}

              {email ? (
                <Button
                  component="a"
                  href={`mailto:${email}`}
                  variant="text"
                  size={compact ? "medium" : "large"}
                  startIcon={<EmailOutlinedIcon />}
                  sx={{ borderRadius: 999, fontWeight: 800 }}
                  fullWidth
                >
                  {t("agent_email")}
                </Button>
              ) : null}
            </Stack>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
